/**
 * The three lane cards at the top of the Orders page: Deliveries, Collections,
 * ETAs. With the open-count headline, each leads with what is NOT done (the
 * work), then says how much is:
 *
 *   4  open
 *   184 of 188 · 97.9% delivered
 *   341 of 363 lifts
 *
 * The percentage is the projection's own `completion_pct`; nothing here
 * computes one. ETAs lead with orders already outside their ETA, derived by
 * the page from the open legs (see utils/ordersExceptions).
 *
 * By default the cards lead with the percentage instead (`headline:
 * 'percent'`); a user can switch to the open count in Customise layout. The
 * same two figures show either way; only which one is large changes:
 *
 *   97.9%  delivered
 *   4 open · 184 of 188
 *
 * With nothing due (no completion %), the large figure is a dash, never 0%.
 */

import type { OperationalProgress, OrdersDailyEtaProgress } from '../../types/ordersDailyTruth';
import type { KeyboardEvent } from 'react';
import { formatLifts, formatPctOneDecimal } from '../../utils/ordersDailyPresentation';
import type { OrdersExceptionLane } from '../../utils/ordersExceptions';
import { ETA_WARNING_OPTIONS, type CardHeadline, type EtaWarningMinutes } from '../../types/ordersUiPreferences';

const TONE: Record<OrdersExceptionLane, { border: string; ring: string; label: string }> = {
  deliveries: { border: 'border-t-success', ring: 'ring-success/60', label: 'Deliveries' },
  collections: { border: 'border-t-warning', ring: 'ring-warning/60', label: 'Collections' },
  etas: { border: 'border-t-ring', ring: 'ring-ring/60', label: 'ETAs' },
};

interface LaneCardProps {
  lane: OrdersExceptionLane;
  /** How many are not done. Undefined while loading or unknown. */
  open: number | undefined;
  /** The large figure, already formatted; undefined while loading or unknown. */
  big: string | undefined;
  bigLabel: string;
  /** Percent headline: neutral, as a share is not itself good or bad. */
  bigNeutral?: boolean;
  /** "184 of 188 · 97.9% delivered", or "4 open · 184 of 188" under a percent headline. */
  progressLine: string | null;
  /** "341 of 363 lifts" */
  liftsLine: string | null;
  /** Small extra line, e.g. "3 more overdue from earlier days". */
  note?: string | null;
  title?: string;
  selected: boolean;
  loading?: boolean;
  onSelect: () => void;
}

function LaneCard({
  lane, open, big, bigLabel, bigNeutral, progressLine, liftsLine, note, title, selected, loading, onSelect,
}: LaneCardProps) {
  const tone = TONE[lane];
  const clear = open === 0;
  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onSelect();
    }
  };
  return (
    <div
      role="button"
      tabIndex={0}
      id={`kpi-lane-${lane}`}
      onClick={onSelect}
      onKeyDown={onKeyDown}
      aria-pressed={selected}
      title={title}
      className={`flex flex-col items-stretch text-left cursor-pointer rounded-lg border border-border border-t-4 ${tone.border} bg-card/50 p-4 sm:p-5 transition-colors hover:bg-card focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-chart-5 ${
        selected ? `ring-2 ${tone.ring} bg-card` : ''
      }`}
    >
      <div className="text-xs font-semibold uppercase tracking-wide-3 text-muted-foreground">{tone.label}</div>
      <div className="mt-2 flex items-baseline gap-3 flex-wrap">
        <span
          className={`text-6xl sm:text-7xl font-bold tabular-nums leading-none ${
            big === undefined || open === undefined ? 'text-muted-foreground'
              : big === NO_PCT ? 'text-muted-foreground'
                : clear ? 'text-success'
                  : bigNeutral ? 'text-foreground' : 'text-destructive'
          }`}
        >
          {loading || big === undefined ? '…' : big}
        </span>
        <span className="text-lg font-medium text-foreground">{bigLabel}</span>
      </div>
      {progressLine && <div className="mt-2 text-sm text-foreground tabular-nums">{progressLine}</div>}
      {liftsLine && <div className="mt-0.5 text-sm text-muted-foreground tabular-nums">{liftsLine}</div>}
      {note && <div className="mt-2 text-xs text-muted-foreground">{note}</div>}
    </div>
  );
}

/** Shown instead of a percentage when nothing is due; grey, as it is neither good nor bad. */
const NO_PCT = '–';

/** The percent headline: the projection's own %, or a dash when nothing is due. */
function pctHeadline(pct: number | null | undefined): string {
  return pct != null ? formatPctOneDecimal(pct) : NO_PCT;
}

function progressText(progress: OperationalProgress | undefined, verb: string, headline: CardHeadline): { line: string | null; lifts: string | null } {
  if (!progress) return { line: null, lifts: null };
  const ofTotal = `${progress.completed.consignments.toLocaleString()} of ${progress.total.consignments.toLocaleString()}`;
  const line = headline === 'percent'
    ? `${progress.open.consignments.toLocaleString()} open · ${ofTotal}`
    : `${ofTotal}${progress.completion_pct != null ? ` · ${formatPctOneDecimal(progress.completion_pct)}` : ''} ${verb}`;
  const lifts = `${formatLifts(progress.completed.lifts)} of ${formatLifts(progress.total.lifts)} lifts`;
  return { line, lifts };
}

function etaProgressText(
  eta: OrdersDailyEtaProgress | null | undefined,
  headline: CardHeadline,
  outsideEta: number | undefined,
): { line: string | null; lifts: string | null } {
  if (!eta) return { line: null, lifts: null };
  const ofTotal = `${eta.on_time.consignments.toLocaleString()} of ${eta.total.consignments.toLocaleString()}`;
  const line = headline === 'percent'
    ? `${outsideEta != null ? `${outsideEta.toLocaleString()} outside ETA now · ` : ''}${ofTotal} with an ETA`
    : `${ofTotal}${eta.completion_pct != null ? ` · ${formatPctOneDecimal(eta.completion_pct)}` : ''} on time`;
  const lifts = `${formatLifts(eta.on_time.lifts)} of ${formatLifts(eta.total.lifts)} lifts`;
  return { line, lifts };
}

function plural(n: number, one: string, many = `${one}s`): string {
  return `${n.toLocaleString()} ${n === 1 ? one : many}`;
}

function warningLabel(minutes: EtaWarningMinutes): string {
  return ETA_WARNING_OPTIONS.find((o) => o.minutes === minutes)?.label.toLowerCase() ?? `${minutes} minutes`;
}

export interface OrdersLaneCardsProps {
  deliveries?: OperationalProgress;
  collections?: OperationalProgress;
  eta?: OrdersDailyEtaProgress | null;
  /** Open legs from earlier days, which the headline figures leave out. */
  overdueDeliveries: number;
  overdueCollections: number;
  /** Open deliveries already outside their ETA. */
  outsideEta: number;
  /** Open deliveries whose ETA runs out inside the warning window. */
  expiringEta: number;
  etaWarnMinutes: EtaWarningMinutes;
  /** Which figure leads each card (the user's Customise layout choice). */
  headline: CardHeadline;
  /** False when the period has no ETA to judge (no estimates received). */
  etaAvailable: boolean;
  /** The due / collection date behind the figures is not yet confirmed. */
  deliveriesProvisional?: boolean;
  collectionsProvisional?: boolean;
  selected: OrdersExceptionLane;
  loading?: boolean;
  onSelect: (lane: OrdersExceptionLane) => void;
}

export default function OrdersLaneCards({
  deliveries, collections, eta, overdueDeliveries, overdueCollections, outsideEta, expiringEta,
  etaWarnMinutes, headline, etaAvailable, deliveriesProvisional, collectionsProvisional, selected, loading, onSelect,
}: OrdersLaneCardsProps) {
  const pct = headline === 'percent';
  const d = progressText(deliveries, 'delivered', headline);
  const c = progressText(collections, 'collected', headline);
  const outside = etaAvailable ? outsideEta : undefined;
  const e = etaProgressText(eta, headline, outside);
  const count = (n: number | undefined) => (n === undefined ? undefined : n.toLocaleString());
  const share = (progress: { completion_pct: number | null } | null | undefined) =>
    (progress ? pctHeadline(progress.completion_pct) : undefined);
  const noteFor = (overdue: number, provisional?: boolean) => {
    const parts: string[] = [];
    if (overdue > 0) parts.push(`Plus ${plural(overdue, 'more')} overdue from earlier days`);
    if (provisional) parts.push('Date not yet confirmed');
    return parts.length ? parts.join('. ') : null;
  };
  const etaNote = !etaAvailable
    ? 'No ETA estimates in this period'
    : expiringEta > 0
      ? `${plural(expiringEta, 'more')} expiring within ${warningLabel(etaWarnMinutes)}`
      : null;
  return (
    <section id="orders-lane-cards" aria-label="Deliveries, collections and ETAs" className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <LaneCard
        lane="deliveries"
        open={deliveries?.open.consignments}
        big={pct ? share(deliveries) : count(deliveries?.open.consignments)}
        bigLabel={pct ? 'delivered' : 'open'}
        bigNeutral={pct}
        progressLine={d.line}
        liftsLine={d.lifts}
        note={noteFor(overdueDeliveries, deliveriesProvisional)}
        selected={selected === 'deliveries'}
        loading={loading && !deliveries}
        onSelect={() => onSelect('deliveries')}
      />
      <LaneCard
        lane="collections"
        open={collections?.open.consignments}
        big={pct ? share(collections) : count(collections?.open.consignments)}
        bigLabel={pct ? 'collected' : 'open'}
        bigNeutral={pct}
        progressLine={c.line}
        liftsLine={c.lifts}
        note={noteFor(overdueCollections, collectionsProvisional) ?? 'Collections keep arriving through the day'}
        selected={selected === 'collections'}
        loading={loading && !collections}
        onSelect={() => onSelect('collections')}
      />
      <LaneCard
        lane="etas"
        open={outside}
        big={pct ? (outside === undefined ? undefined : share(eta)) : count(outside)}
        bigLabel={pct ? 'on time' : 'outside ETA now'}
        bigNeutral={pct}
        progressLine={e.line}
        liftsLine={e.lifts}
        note={etaNote}
        title="Delivered orders without an ETA estimate are left out of the percentage"
        selected={selected === 'etas'}
        loading={loading && !eta}
        onSelect={() => onSelect('etas')}
      />
    </section>
  );
}
